import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import {
  getRpmAccount,
  listRpmAccounts,
  patchRpmAccount,
  validateRpmAccountDeletable as validateRpmAccountDeletableApi,
  deleteRpmAccount
} from '../Api/RpmAccount/RpmAccountApi';
import { addError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { DrawerData } from './Drawer';

export interface RpmAccount {
  accountCustomerName: string;
  accountId: string;
  accountNumber: string;
  supplier: string;
  supplierId: string;
  serialNumber: string | null;
  addedDate: string | null;
  assetCount: string | null;
  assignedRep: string | null;
  assignedRepId: number | null;
  assignedAgency: string | null;
  cancelDate: string | null;
  disconnectDate: string | null;
  notesFromTsb: string | null;
  circuitId: string | null;
  accountGroup: string | null;
  reportedAgency: string | null;
  address: string | null;
  ultimateParentId: string | null;
  ultimateParent: string | null;
  linkId: string | null;
  source: string | null;
  firstCommissionRun: string | null;
  lastCommissionRun: string | null;
  latestMrc: string | null;
  latestMrrPercent: string | null;
  latestMrr: string | null;
  lastMrc: string | null;
  lastMrrPercent: string | null;
  lastMrr: string | null;
  paCount: string | null;
  linkCount: string | null;
  ticketCount: string | null;
}

const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const rpmAccountSubject = new BehaviorSubject<RpmAccount>({} as RpmAccount);
const rpmAccountDetailSubject = new BehaviorSubject<DrawerData | undefined>(undefined);
const cruxRpmAccountsSubject = new BehaviorSubject<PaginatedResponse<RpmAccount>>({} as PaginatedResponse<RpmAccount>);
const cruxRpmAccountsByAssetSubject = new BehaviorSubject<PaginatedResponse<RpmAccount>>(
  {} as PaginatedResponse<RpmAccount>
);
const cruxRpmAccountsByAssetDrawerSubject = new BehaviorSubject<PaginatedResponse<RpmAccount>>(
  {} as PaginatedResponse<RpmAccount>
);
const rpmAcctByTicketIdSubject = new BehaviorSubject<PaginatedResponse<RpmAccount>>(
  {} as PaginatedResponse<RpmAccount>
);
const rpmAcctByTicketIdDrawerSubject = new BehaviorSubject<PaginatedResponse<RpmAccount>>(
  {} as PaginatedResponse<RpmAccount>
);
const tallyRpmAccountsByPAssetSubject = new BehaviorSubject<PaginatedResponse<RpmAccount>>(
  {} as PaginatedResponse<RpmAccount>
);
const tallyRpmAccountsByPAssetDrawerSubject = new BehaviorSubject<PaginatedResponse<RpmAccount>>(
  {} as PaginatedResponse<RpmAccount>
);
const rpmAccountLoadingSubject = new BehaviorSubject<boolean>(false);
const cruxRpmAccountsLoadingSubject = new BehaviorSubject<boolean>(false);
const rpmAcctByTicketIdLoadingSubject = new BehaviorSubject<boolean>(false);
const rpmAcctByTicketIdLoadingDrawerSubject = new BehaviorSubject<boolean>(false);

const cruxRpmAccountsByAssetLoadingSubject = new BehaviorSubject<boolean>(false);
const cruxRpmAccountsByAssetLoadingDrawerSubject = new BehaviorSubject<boolean>(false);
const tallyRpmAccountsByPAssetLoadingSubject = new BehaviorSubject<boolean>(false);
const tallyRpmAccountsByPAssetLoadingDrawerSubject = new BehaviorSubject<boolean>(false);
const updatingRpmAccountSubject = new BehaviorSubject<boolean>(false);

const clearError = () => addError('rpm-account', undefined);

export const getRpmAccountSubject = (): BehaviorSubject<RpmAccount> => rpmAccountSubject;
export const getRpmAccountDetailSubject = (): BehaviorSubject<DrawerData | undefined> => rpmAccountDetailSubject;

export const getCruxRpmAccountsSubject = (): BehaviorSubject<PaginatedResponse<RpmAccount>> => cruxRpmAccountsSubject;
export const getCruxRpmAccountsByAssetSubject = (): BehaviorSubject<PaginatedResponse<RpmAccount>> =>
  cruxRpmAccountsByAssetSubject;
export const getCruxRpmAccountsByAssetDrawerSubject = (): BehaviorSubject<PaginatedResponse<RpmAccount>> =>
  cruxRpmAccountsByAssetDrawerSubject;
export const getRpmAcctByTicketIdSubject = (): BehaviorSubject<PaginatedResponse<RpmAccount>> =>
  rpmAcctByTicketIdSubject;
export const getRpmAcctByTicketIdDrawerSubject = (): BehaviorSubject<PaginatedResponse<RpmAccount>> =>
  rpmAcctByTicketIdDrawerSubject;
export const getRpmAcctByTicketIdLoadingSubject = (): BehaviorSubject<boolean> => rpmAcctByTicketIdLoadingSubject;
export const getRpmAcctByTicketIdLoadingDrawerSubject = (): BehaviorSubject<boolean> =>
  rpmAcctByTicketIdLoadingDrawerSubject;
export const getTallyRpmAccountsByPAssetSubject = (): BehaviorSubject<PaginatedResponse<RpmAccount>> =>
  tallyRpmAccountsByPAssetSubject;
export const getTallyRpmAccountsByPAssetDrawerSubject = (): BehaviorSubject<PaginatedResponse<RpmAccount>> =>
  tallyRpmAccountsByPAssetDrawerSubject;

export const getRpmAccountLoadingSubject = (): BehaviorSubject<boolean> => rpmAccountLoadingSubject;
export const getCruxRpmAccountsLoadingSubject = (): BehaviorSubject<boolean> => cruxRpmAccountsLoadingSubject;
export const getCruxRpmAccountsByAssetLoadingSubject = (): BehaviorSubject<boolean> =>
  cruxRpmAccountsByAssetLoadingSubject;
export const getCruxRpmAccountsByAssetLoadingDrawerSubject = (): BehaviorSubject<boolean> =>
  cruxRpmAccountsByAssetLoadingDrawerSubject;
export const getTallyRpmAccountsByPAssetLoadingSubject = (): BehaviorSubject<boolean> =>
  tallyRpmAccountsByPAssetLoadingSubject;
export const getTallyRpmAccountsByPAssetLoadingDrawerSubject = (): BehaviorSubject<boolean> =>
  tallyRpmAccountsByPAssetLoadingDrawerSubject;

export const getRpmAccountById = async (accountId: string): Promise<RpmAccount> => {
  const rpmAccount = cruxRpmAccountsSubject?.getValue()?.data?.find((a) => a?.accountId === accountId);

  if (rpmAccount) {
    return rpmAccount;
  } else {
    const { data, error } = await getRpmAccount(accountId);
    if (error) addError('rpm-account', error.message);
    if (data) return data;
  }

  return {} as RpmAccount;
};

export const updateRpmAccountDetailSubject = (rpmAccount: DrawerData) => rpmAccountDetailSubject.next(rpmAccount);

const updateRpmAccountToCruxRpmAccountsSubject = (rpmAccount: RpmAccount) => {
  const currentAccounts = [...(cruxRpmAccountsSubject.getValue().data || [])];
  const existingAccountIndex = currentAccounts.findIndex((a) => a.accountId === rpmAccount.accountId);
  if (existingAccountIndex !== -1) {
    currentAccounts.splice(existingAccountIndex, 1, rpmAccount);
    cruxRpmAccountsSubject.next({ ...cruxRpmAccountsSubject.getValue(), data: currentAccounts });
  }
};

export const fetchRpmAccount = async (id: string, updateDrawer?: boolean) => {
  rpmAccountLoadingSubject.next(true);

  const { data, error } = await getRpmAccount(id);

  if (data) {
    clearError();
    const detailSubject = getRpmAccountDetailSubject().getValue() as RpmAccount | undefined;
    if (updateDrawer || detailSubject?.accountId === data.accountId) updateRpmAccountDetailSubject(data);
    const subject = getRpmAccountSubject().getValue();
    if (!updateDrawer || !subject.accountId || subject.accountId === data.accountId) rpmAccountSubject.next(data);

    updateRpmAccountToCruxRpmAccountsSubject(data);
  }
  if (error) {
    addError('rpm-account', error.message);
    const statusCode = error.errorResponse?.status || 500;
    return statusCode;
  }

  rpmAccountLoadingSubject.next(false);
};

export const fetchRpmAccounts = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (cruxRpmAccountsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  cruxRpmAccountsLoadingSubject.next(true);

  const { data, error } = await listRpmAccounts(filters);

  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError();
    cruxRpmAccountsSubject.next(data);
  }
  if (error) addError('rpm-account', error.message);

  newFetchNumber === currentFetchNumber && cruxRpmAccountsLoadingSubject.next(false);
};

export const updateRpmAccount = async (rpmAccount: RpmAccount, updateDrawer?: boolean) => {
  updatingRpmAccountSubject.next(true);

  const { data, error } = await patchRpmAccount(rpmAccount);

  updatingRpmAccountSubject.next(false);

  if (error) addError('rpm-account', error.message);
  if (data) {
    clearError();
    fetchRpmAccount(rpmAccount.accountId, updateDrawer);
  }
  return { data, error };
};

export const fetchRpmAccountsByAsset =
  (id: string, forDrawer = false) =>
  async (filters?: ApiAdvancedFilter) => {
    const loading = forDrawer ? cruxRpmAccountsByAssetLoadingDrawerSubject : cruxRpmAccountsByAssetLoadingSubject;
    const subject = forDrawer ? cruxRpmAccountsByAssetDrawerSubject : cruxRpmAccountsByAssetSubject;
    loading.next(true);

    const mergedFilters: ApiAdvancedFilter = {
      page: 1,
      pageSize: 50,
      ...filters,
      orders: filters?.orders ?? [{ sort: 'accountNumber', direction: 'desc' }],
      filters: [...(filters?.filters || []), { field: 'assetId', operator: 'equals', value: id }]
    };
    const { data, error } = await listRpmAccounts(mergedFilters);

    if (data) {
      clearError();
      subject.next(data);
    }
    if (error) addError('rpm-account', error.message);

    loading.next(false);
  };

export const fetchRPMAcctsByTicketId =
  (id: string, forDrawer = false) =>
  async (filters?: ApiAdvancedFilter): Promise<PaginatedResponse<RpmAccount> | undefined> => {
    const loading = forDrawer ? rpmAcctByTicketIdLoadingDrawerSubject : rpmAcctByTicketIdLoadingSubject;
    const subject = forDrawer ? rpmAcctByTicketIdDrawerSubject : rpmAcctByTicketIdSubject;
    loading.next(true);
    const mergedFilters: ApiAdvancedFilter = {
      page: 1,
      pageSize: 50,
      ...filters,
      orders: filters?.orders ?? [{ sort: 'accountNumber', direction: 'desc' }],
      filters: [...(filters?.filters || []), { field: 'ticketId', operator: 'equals', value: id }]
    };
    const { data, error } = await listRpmAccounts(mergedFilters);

    if (error) addError('ticket', error.message);

    loading.next(false);

    if (data) {
      clearError();
      subject.next(data);
      return data;
    }
  };

export const fetchRpmAccountsByPAsset =
  (id: string, forDrawer = false) =>
  async (filters?: ApiAdvancedFilter) => {
    const loading = forDrawer ? tallyRpmAccountsByPAssetLoadingDrawerSubject : tallyRpmAccountsByPAssetLoadingSubject;
    const subject = forDrawer ? tallyRpmAccountsByPAssetDrawerSubject : tallyRpmAccountsByPAssetSubject;
    loading.next(true);

    const mergedFilters: ApiAdvancedFilter = {
      page: 1,
      pageSize: 50,
      ...filters,
      orders: filters?.orders ?? [{ sort: 'accountNumber', direction: 'desc' }],
      filters: [...(filters?.filters || []), { field: 'pAssetId', operator: 'equals', value: id }]
    };
    const { data, error } = await listRpmAccounts(mergedFilters);

    if (data) {
      clearError();
      subject.next(data);
    }
    if (error) addError('rpm-account', error.message);

    loading.next(false);
  };

export const validateRpmAccountDeletable = async (accountId: string): Promise<boolean | string> => {
  const { data, error } = await validateRpmAccountDeletableApi(accountId);

  if (data) {
    return data.deletable;
  }

  if (error) {
    return error.message;
  }

  return false;
};

export const removeRpmAccount = async (accountId: string): Promise<boolean> => {
  const { error } = await deleteRpmAccount(accountId);

  if (error) {
    addError('rpm-account', error.message);
    return false;
  }

  return true;
};
